import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Plus, LogOut, ArrowRight, Calendar, MapPin, AlertTriangle, Loader2 } from "lucide-react";
import { toast } from "sonner";

import { projectsApi } from "@/lib/api";
import { useAuth } from "@/lib/auth";

const LS_PROJECT_KEY = "ps.currentProjectId";

/**
 * Project picker — the first thing a freshly-authenticated user sees.
 *
 * Lists every project the user has membership on (server-side: owner +
 * explicit memberships + ADMIN_EMAILS bypass), grouped by status. Clicking
 * a card persists the choice and routes to the schedule.
 *
 * Falls back to a first-run create CTA when the user has zero projects.
 */
export default function ProjectsPage() {
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    projectsApi.list()
      .then((list) => { if (!cancelled) setProjects(list || []); })
      .catch((e) => toast.error(e.response?.data?.detail || "Failed to load projects"))
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, []);

  const pick = (id) => {
    try {
      localStorage.setItem(LS_PROJECT_KEY, id);
    } catch (e) {
      console.warn("project pref persist failed", e);
    }
    navigate("/");
  };

  const { active, draft, archived } = useMemo(() => groupByStatus(projects), [projects]);

  return (
    <div className="min-h-screen bg-bg-base text-ink">
      {/* Header */}
      <header className="border-b border-line-strong bg-bg-elevated">
        <div className="max-w-6xl mx-auto px-6 py-5 flex items-center gap-4">
          <div className="w-8 h-8 bg-amber" />
          <div className="flex-1">
            <div className="mono text-2xs uppercase tracking-[0.2em] text-amber">CONSOLE</div>
            <div className="text-sm text-ink-dim mt-0.5">{user?.name} · {user?.email}</div>
          </div>
          <button
            onClick={async () => { await logout(); navigate("/login"); }}
            className="btn-ghost"
            data-testid="projects-logout"
            title="Sign out"
          >
            <LogOut className="w-3 h-3" /> SIGN OUT
          </button>
        </div>
      </header>

      {/* Body */}
      <main className="max-w-6xl mx-auto px-6 py-8">
        <div className="flex items-end justify-between mb-6">
          <div>
            <h1 className="text-3xl font-semibold tracking-tight">Pick a project</h1>
            <p className="text-ink-dim text-sm mt-1">
              {projects.length === 0
                ? "You don't have any projects yet — let's create one."
                : `${projects.length} project${projects.length === 1 ? "" : "s"} available.`}
            </p>
          </div>
          <button
            onClick={() => navigate("/?new=1")}
            className="btn-amber"
            data-testid="projects-new-btn"
          >
            <Plus className="w-3 h-3" /> NEW PROJECT
          </button>
        </div>

        {loading ? (
          <div className="flex items-center justify-center py-24 text-ink-mute mono text-2xs uppercase tracking-widest">
            <Loader2 className="w-3 h-3 animate-spin mr-2" /> loading projects…
          </div>
        ) : projects.length === 0 ? (
          <EmptyState onCreate={() => navigate("/?new=1")} />
        ) : (
          <div className="space-y-8">
            <Section title="ACTIVE" projects={active} onPick={pick} />
            <Section title="DRAFT" projects={draft} onPick={pick} />
            <Section title="ARCHIVED" projects={archived} onPick={pick} muted />
          </div>
        )}
      </main>
    </div>
  );
}

function Section({ title, projects, onPick, muted }) {
  if (!projects || projects.length === 0) return null;
  return (
    <section data-testid={`projects-section-${title.toLowerCase()}`}>
      <div className="mono text-2xs uppercase tracking-[0.2em] text-amber mb-3">{title} · {projects.length}</div>
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
        {projects.map((p) => <ProjectCard key={p.id} project={p} onPick={onPick} muted={muted} />)}
      </div>
    </section>
  );
}

function ProjectCard({ project, onPick, muted }) {
  const fmtRange = (a, b) => {
    if (!a) return "—";
    if (!b || a === b) return a;
    return `${a} → ${b}`;
  };
  const drift = project.drift_min || 0;
  return (
    <button
      onClick={() => onPick(project.id)}
      className={
        "group text-left bg-bg-surface border border-line hover:border-amber hover:bg-amber-glow/30 transition-colors p-4 flex flex-col gap-2 " +
        (muted ? "opacity-60 hover:opacity-100" : "")
      }
      data-testid={`project-card-${project.id}`}
    >
      <div className="flex items-start justify-between gap-2">
        <div className="text-sm text-ink font-semibold truncate group-hover:text-amber transition-colors">{project.name}</div>
        <ArrowRight className="w-3 h-3 text-ink-mute group-hover:text-amber transition-colors shrink-0 mt-1" />
      </div>
      <div className="mono text-2xs uppercase tracking-widest text-ink-mute flex items-center gap-3 flex-wrap">
        <span className="flex items-center gap-1">
          <Calendar className="w-2.5 h-2.5" />
          {fmtRange(project.start_date, project.end_date)}
        </span>
        {project.venue_name && (
          <span className="flex items-center gap-1">
            <MapPin className="w-2.5 h-2.5" />
            {project.venue_name}
          </span>
        )}
      </div>
      <div className="mt-1 flex items-center gap-2 text-2xs">
        <span className="mono uppercase tracking-widest text-ink-dim">{project.status || "active"}</span>
        {drift !== 0 && (
          <span className="mono uppercase tracking-widest text-red flex items-center gap-1" title="Total drift across all blocks">
            <AlertTriangle className="w-2.5 h-2.5" />
            DRIFT {drift > 0 ? "+" : ""}{drift}m
          </span>
        )}
      </div>
    </button>
  );
}

function EmptyState({ onCreate }) {
  return (
    <div className="border border-dashed border-line p-12 text-center bg-bg-surface">
      <div className="mono text-2xs uppercase tracking-[0.2em] text-amber mb-2">FIRST RUN</div>
      <div className="text-sm text-ink mb-1">No projects yet</div>
      <div className="text-2xs text-ink-mute mb-6">Spin one up — rooms, crew, schedule blocks get configured next.</div>
      <button onClick={onCreate} className="btn-amber" data-testid="projects-create-first">
        <Plus className="w-3 h-3" /> CREATE FIRST PROJECT
      </button>
    </div>
  );
}

function groupByStatus(list) {
  const groups = { active: [], draft: [], archived: [] };
  for (const p of list) {
    const s = (p.status || "active").toLowerCase();
    if (s === "archived" || s === "cancelled" || s === "complete") groups.archived.push(p);
    else if (s === "draft") groups.draft.push(p);
    else groups.active.push(p);
  }
  // Sort each group by most recently updated.
  for (const k of Object.keys(groups)) {
    groups[k].sort((a, b) => String(b.updated_at || "").localeCompare(String(a.updated_at || "")));
  }
  return groups;
}
